/**
 * Header section for TransactionAnalyzer
 * Contains title, action buttons, file upload, stored marketplaces, loading/error states
 */

import React from 'react';
import { Upload, FileSpreadsheet, RefreshCw, AlertCircle, Save, Database, Trash2, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';
import type { MarketplaceCode } from '../../types/transaction';
import { MARKETPLACE_CONFIGS } from '../../constants/marketplaces';

interface StoredFileMetadata {
  id: string;
  marketplace: string;
  fileName: string;
  uploadDate: Date;
  dataCount: number;
  dateRange: { start: Date; end: Date };
  sizeInBytes: number;
}

interface TransactionHeaderProps {
  isAdmin: boolean;
  allDataLength: number;
  loading: boolean;
  error: string | null;
  validationWarnings: string[];

  // File upload
  showFileUpload: boolean;
  setShowFileUpload: (v: boolean) => void;
  saveToDBAfterLoad: boolean;
  setSaveToDBAfterLoad: (v: boolean) => void;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // Stored marketplaces
  storedFiles: StoredFileMetadata[];
  showStoredMarketplaces: boolean;
  setShowStoredMarketplaces: (v: boolean) => void;
  removeMarketplace: (code: string) => void;

  // Export
  exportToPDF: () => void;
  exportToExcel: () => void;
  hasAnalytics: boolean;

  // Clear all
  onClearAll: () => void;
}

const TransactionHeader: React.FC<TransactionHeaderProps> = ({
  isAdmin,
  allDataLength,
  loading,
  error,
  validationWarnings,
  showFileUpload, setShowFileUpload,
  saveToDBAfterLoad, setSaveToDBAfterLoad,
  handleFileUpload,
  storedFiles,
  showStoredMarketplaces, setShowStoredMarketplaces,
  removeMarketplace,
  exportToPDF, exportToExcel,
  hasAnalytics: _hasAnalytics,
  onClearAll,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 no-print">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            Transaction Analyzer
          </h1>
          <p className="text-slate-600">Upload your transaction reports and analyze them in detail</p>
        </div>

        {/* Action Buttons - Two Rows */}
        <div className="flex flex-col gap-2">
          {/* First Row: Upload, Stored, Refresh, Clear */}
          <div className="flex items-center gap-2 justify-end">
            {/* File Upload Toggle - Admin Only */}
            {isAdmin && (
              <button
                onClick={() => setShowFileUpload(!showFileUpload)}
                className="flex items-center justify-center gap-2 px-4 py-2 min-w-[120px] bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                title="Upload transaction reports (xlsx, xls, csv)"
              >
                <Upload className="w-4 h-4" />
                <span className="text-sm font-medium">Upload</span>
                {showFileUpload ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}

            {/* Stored Marketplaces Toggle */}
            {storedFiles.length > 0 && (
              <button
                onClick={() => setShowStoredMarketplaces(!showStoredMarketplaces)}
                className="flex items-center justify-center gap-2 px-4 py-2 min-w-[120px] bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                title="Stored marketplaces"
              >
                <Database className="w-4 h-4" />
                <span className="text-sm font-medium">Stored ({storedFiles.length})</span>
                {showStoredMarketplaces ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}

            {/* Clear All Data - Admin Only */}
            {isAdmin && (
              <button
                onClick={onClearAll}
                className="flex items-center justify-center gap-2 px-4 py-2 min-w-[100px] bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                title="Clear all data (API + Cache)"
              >
                <Trash2 className="w-4 h-4" />
                <span className="text-sm font-medium">Clear</span>
              </button>
            )}
          </div>

          {/* Second Row: PDF, Excel - Only show when data exists and user is admin */}
          {allDataLength > 0 && isAdmin && (
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={exportToPDF}
                className="flex items-center justify-center gap-2 px-4 py-2 min-w-[120px] bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                title="PDF olarak indir"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-sm font-medium">PDF</span>
              </button>

              <button
                onClick={exportToExcel}
                className="flex items-center justify-center gap-2 px-4 py-2 min-w-[120px] bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                title="Excel olarak indir"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-sm font-medium">Excel</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* File Upload Section - Collapsible */}
      {showFileUpload && (
        <div className="mt-4 border-t-2 border-dashed border-slate-300 pt-6">
          <div className="p-8 text-center bg-slate-50 rounded-lg hover:border-blue-400 transition-colors">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              disabled={loading}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-lg font-medium text-slate-700 mb-1">
                Upload transaction reports
              </p>
              <p className="text-sm text-slate-500">
                Upload your transaction reports (.xlsx, .xls, .csv)
              </p>
            </label>

            <div className="mt-4 pt-4 border-t border-slate-200">
              <label className="flex items-center justify-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveToDBAfterLoad}
                  onChange={(e) => setSaveToDBAfterLoad(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-slate-600">
                  <Save className="w-4 h-4 inline mr-1" />
                  Auto-save files
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Stored Marketplaces Section - Collapsible */}
      {showStoredMarketplaces && storedFiles.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {storedFiles.map((stored) => {
              const mpConfig = MARKETPLACE_CONFIGS[stored.marketplace as MarketplaceCode];
              const dateStart = new Date(stored.dateRange.start).toLocaleDateString('en-US');
              const dateEnd = new Date(stored.dateRange.end).toLocaleDateString('en-US');

              return (
                <div
                  key={stored.id}
                  className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:shadow-lg hover:border-blue-400 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Database className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-slate-800">
                          {mpConfig?.name || stored.marketplace}
                        </h3>
                        <span className="text-xs font-medium text-blue-600">
                          {stored.marketplace}
                        </span>
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => removeMarketplace(stored.marketplace)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete this marketplace"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-2 mt-4">
                    <div className="flex items-center justify-between py-2 border-t border-slate-100">
                      <span className="text-xs text-slate-500">Transaction Count</span>
                      <span className="text-sm font-semibold text-slate-800">
                        {stored.dataCount.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-slate-100">
                      <span className="text-xs text-slate-500">Tarih Aralığı</span>
                      <span className="text-xs text-slate-700">
                        {dateStart}
                      </span>
                    </div>
                    <div className="flex items-center justify-end pb-1">
                      <span className="text-xs text-slate-700">
                        {dateEnd}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-4 flex items-center gap-2 text-blue-600">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Processing files...</span>
        </div>
      )}

      {validationWarnings.length > 0 && (
        <div className="mt-4 space-y-2">
          {validationWarnings.map((warning, idx) => (
            <div key={idx} className="flex items-center gap-2 text-yellow-700 bg-yellow-50 p-3 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{warning}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default TransactionHeader;
